import { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MonteCarloPercentileBand } from "../../lib/util/stress-test.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * Monte Carlo confidence-band chart. Renders the p10/p50/p90 envelope of
 * 1,000+ stochastic future trajectories.
 *
 * Visual:
 *   - Stacked area: invisible p10 baseline + visible p90-minus-p10 fill =
 *     the "middle 80%" band. Token-driven accent fill at low opacity.
 *   - p50 (median) line painted on top in full accent.
 *
 * Privacy mode masks Y-axis + tooltip values; chart shape stays visible
 * because the spread itself is the lesson.
 */

interface MonteCarloChartProps {
  readonly band: readonly MonteCarloPercentileBand[];
  readonly hideValues: boolean;
}

interface ChartPoint {
  readonly year_offset: number;
  readonly p10_cents: number;
  readonly p50_cents: number;
  readonly p90_cents: number;
  /** Derived: p90 - p10. Stacked on top of p10 to render the middle band. */
  readonly band_cents: number;
}

export function MonteCarloChart({ band, hideValues }: MonteCarloChartProps) {
  const data = useMemo<ChartPoint[]>(
    () =>
      band.map((p) => ({
        year_offset: p.year_offset,
        p10_cents: p.p10_cents,
        p50_cents: p.p50_cents,
        p90_cents: p.p90_cents,
        band_cents: Math.max(0, p.p90_cents - p.p10_cents),
      })),
    [band],
  );

  if (data.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-muted)]"
        data-testid="montecarlo-chart-empty"
      >
        Run a Monte Carlo simulation to see the confidence band.
      </div>
    );
  }

  return (
    <div className="h-[280px] w-full" data-testid="montecarlo-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <ComposedChart
          data={data}
          margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
        >
          <defs>
            <linearGradient id="mcBandFill" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--color-accent)"
                stopOpacity={0.30}
              />
              <stop
                offset="100%"
                stopColor="var(--color-accent)"
                stopOpacity={0.05}
              />
            </linearGradient>
          </defs>
          <CartesianGrid strokeOpacity={0.1} vertical={false} />
          <XAxis
            dataKey="year_offset"
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={(y: number) => `Y${y}`}
            minTickGap={20}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={(v: number) =>
              hideValues ? "••••" : formatCentsCompact(v)
            }
            width={64}
          />
          <Tooltip
            content={
              hideValues
                ? () => null
                : ({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) {
                      return null;
                    }
                    const point = payload[0]?.payload as
                      | ChartPoint
                      | undefined;
                    if (!point) return null;
                    return (
                      <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-xs shadow-[var(--shadow-card)]">
                        <div className="font-medium text-[var(--color-text)]">
                          Year {label}
                        </div>
                        <div className="mt-1 grid grid-cols-[auto_auto] gap-x-3 font-mono tabular-nums">
                          <span className="text-[var(--color-muted)]">p90</span>
                          <span className="text-[var(--color-text)]">
                            {formatCentsCompact(point.p90_cents)}
                          </span>
                          <span className="text-[var(--color-accent)]">p50</span>
                          <span className="text-[var(--color-accent)]">
                            {formatCentsCompact(point.p50_cents)}
                          </span>
                          <span className="text-[var(--color-muted)]">p10</span>
                          <span className="text-[var(--color-text)]">
                            {formatCentsCompact(point.p10_cents)}
                          </span>
                        </div>
                      </div>
                    );
                  }
            }
          />
          {/* Invisible p10 baseline holds the stack at the floor. */}
          <Area
            type="monotone"
            dataKey="p10_cents"
            stroke="none"
            fill="transparent"
            stackId="confidence"
            isAnimationActive={false}
          />
          {/* Visible band (p90 - p10), token-driven gradient. */}
          <Area
            type="monotone"
            dataKey="band_cents"
            stroke="none"
            fill="url(#mcBandFill)"
            stackId="confidence"
            isAnimationActive={false}
          />
          {/* p50 median line on top. */}
          <Line
            type="monotone"
            dataKey="p50_cents"
            stroke="var(--color-accent)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
